import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import apiClient, { API_ENDPOINTS } from '../utils/api';

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const LoginCard = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 480px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
    border-radius: 20px;
    max-width: 100%;
  }
`;

const Title = styled.h1`
  color: #000000;
  margin-bottom: 2rem;
  font-size: 2rem;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.75rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  margin-top: 0;
  
  @media (max-width: 768px) {
    gap: 0.875rem;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 8px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem;
  }
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    user_id: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        user_id: formData.user_id,
        password: formData.password
      });

      const data = response.data;

      if (data.success) {
        // 토큰과 사용자 정보 저장
        login(data.data.access_token, data.data.user);
        
        // 관리자 여부에 따라 페이지 이동
        if (data.data.user.is_admin) {
          navigate('/admin');
        } else {
          navigate('/member');
        }
      } else {
        const errorMessage = data.detail || '로그인에 실패했습니다.';
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error('로그인 오류:', err);
      const errorMessage = err.response?.data?.detail || '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Title>로그인</Title>
        
        <Form onSubmit={handleSubmit}>
          <Input
            label="아이디"
            type="text"
            value={formData.user_id}
            onChange={(e) => handleInputChange('user_id', e.target.value)}
            placeholder="아이디를 입력하세요"
            required
          />
          
          <Input
            label="비밀번호"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="비밀번호를 입력하세요"
            required
          />
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <ButtonGroup>
            <Button 
              type="submit" 
              variant="primary" 
              size="large" 
              fullWidth 
              disabled={loading}
            >
              {loading ? '로그인 중...' : '로그인'}
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              size="large" 
              fullWidth 
              onClick={() => navigate('/create-account')}
            >
              회원가입
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              size="large" 
              fullWidth 
              onClick={() => navigate('/password-reset')}
            >
              비밀번호 재발급
            </Button>
          </ButtonGroup>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;

